import React, { useState, useEffect } from 'react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'
import TemplateEditor from '../../../components/vision/TemplateEditor'
import ContratosRelatorios from '../../../components/vision/ContratosRelatorios'
import ContratosConfiguracoes from '../../../components/vision/ContratosConfiguracoes'
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Settings,
  BarChart3,
  Search
} from 'lucide-react'

interface Template {
  id: string
  nome: string
  tipo: string
  template_html: string
  variaveis_mapeamento: any
  ativo: boolean
  created_at: string
  updated_at: string
}

const ContratosPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('templates')
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/contratos/templates')
      const data = await response.json()
      
      if (data.data) {
        setTemplates(data.data)
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTemplate = async (templateData: Omit<Template, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const url = selectedTemplate 
        ? `/api/contratos/templates?id=${selectedTemplate.id}`
        : '/api/contratos/templates'
      
      const method = selectedTemplate ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateData)
      })

      if (response.ok) {
        await fetchTemplates()
        setShowTemplateModal(false)
        setSelectedTemplate(null)
      } else {
        const result = await response.json()
        alert('Erro ao salvar template: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error)
      alert('Erro ao salvar template')
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Tem certeza que deseja excluir este template?')) {
      return
    }

    try {
      const response = await fetch(`/api/contratos/templates?id=${templateId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await fetchTemplates()
      } else {
        const result = await response.json()
        alert('Erro ao excluir template: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao excluir template:', error)
      alert('Erro ao excluir template')
    }
  }

  const handleToggleTemplate = async (templateId: string, ativo: boolean) => {
    try {
      const response = await fetch(`/api/contratos/templates?id=${templateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ativo: !ativo })
      })

      if (response.ok) {
        await fetchTemplates()
      } else {
        const result = await response.json()
        alert('Erro ao atualizar template: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao atualizar template:', error)
      alert('Erro ao atualizar template')
    }
  }

  const filteredTemplates = templates.filter(template =>
    template.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.tipo.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const tabs = [
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'relatorios', label: 'Relatórios', icon: BarChart3 },
    { id: 'configuracoes', label: 'Configurações', icon: Settings }
  ]

  return (
    <ProtectedRoute>
      <Layout>
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold gradient-text-orange mb-2">
                Gestão de Contratos
              </h1>
              <p className="text-gray-600">
                Gerencie templates, visualize relatórios e configure o sistema de contratos
              </p>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                          activeTab === tab.id
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                      </button>
                    )
                  })}
                </nav>
              </div>

              <div className="p-6">
                {/* Templates Tab */}
                {activeTab === 'templates' && (
                  <div className="space-y-6">
                    {/* Header da seção */}
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Templates de Contrato</h2>
                        <p className="text-gray-600 mt-1">Gerencie os modelos de contrato do sistema</p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedTemplate(null)
                          setShowTemplateModal(true)
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Template
                      </button>
                    </div>

                    {/* Filtros */}
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 relative">
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                        <input
                          type="text"
                          placeholder="Buscar templates..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    {/* Lista de Templates */}
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                      </div>
                    ) : filteredTemplates.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                          >
                            <div className="p-6">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center">
                                  <FileText className="w-8 h-8 text-orange-500" />
                                  <div className="ml-3">
                                    <h3 className="text-lg font-medium text-gray-900">
                                      {template.nome}
                                    </h3>
                                    <p className="text-sm text-gray-500">{template.tipo}</p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    template.ativo 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {template.ativo ? 'Ativo' : 'Inativo'}
                                  </span>
                                </div>
                              </div>

                              <div className="mt-4">
                                <p className="text-sm text-gray-600">
                                  Atualizado em {formatDate(template.updated_at)}
                                </p>
                              </div>

                              <div className="mt-6 flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      setSelectedTemplate(template)
                                      setShowTemplateModal(true)
                                    }}
                                    className="text-orange-600 hover:text-orange-800 font-medium text-sm"
                                  >
                                    <Edit className="w-4 h-4 inline mr-1" />
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => handleToggleTemplate(template.id, template.ativo)}
                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                  >
                                    {template.ativo ? 'Desativar' : 'Ativar'}
                                  </button>
                                </div>
                                <button
                                  onClick={() => handleDeleteTemplate(template.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum template encontrado</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Comece criando um novo template de contrato.
                        </p>
                        <div className="mt-6">
                          <button
                            onClick={() => {
                              setSelectedTemplate(null)
                              setShowTemplateModal(true)
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Novo Template
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Relatórios Tab */}
                {activeTab === 'relatorios' && (
                  <ContratosRelatorios />
                )}

                {/* Configurações Tab */}
                {activeTab === 'configuracoes' && (
                  <ContratosConfiguracoes />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Editor de Template */}
        {showTemplateModal && (
          <TemplateEditor
            template={selectedTemplate}
            onSave={handleSaveTemplate}
            onClose={() => {
              setShowTemplateModal(false)
              setSelectedTemplate(null)
            }}
          />
        )}
      </Layout>
    </ProtectedRoute>
  )
}

export default ContratosPage
